import * as React from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid2';
import CircularProgress from '@mui/material/CircularProgress';
import * as Device from 'react-device-detect';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import Pagination from '@mui/material/Pagination';
import { useReactiveVar } from '@apollo/client';

import { useSearchEventsLazyQuery, useSearchSpotsLazyQuery, useSearchMembersLazyQuery } from '@graphql';
import { Slide, MobileSlide, SlideProps, OpenChip, OpenText } from '@components';
import { getTrueKeys } from '@utils';
import { loginStatusVar } from '@context';
import { useToastError } from '@hooks';
// @ts-expect-error ts(2307)
import * as helpers from '@components/search/form-fields/spot';
// @ts-expect-error ts(2307)
import { initFilters, baseFilters } from '@components/search/form-fields/spot/initValues';

const getSpotFilters = (searchParams: URLSearchParams, isLoggedIn: boolean) =>
  helpers.removeDefaultConfiguration(
    helpers.removeNullOrEmpty({
      ...initFilters(searchParams, isLoggedIn),
      name: searchParams.get('name') ?? '',
      location: searchParams.get('location') ?? '',
      type: searchParams.get('type') ?? '',
    })
  );

const getEventFilters = (searchParams: URLSearchParams) =>
  helpers.removeNullOrEmpty({
    from: searchParams.get('from'),
    to: searchParams.get('to'),
    categories: searchParams.get('categories'),
    name: searchParams.get('name'),
    location: searchParams.get('location'),
  });

const getMemberFilters = (searchParams: URLSearchParams) =>
  helpers.removeNullOrEmpty({
    name: searchParams.get('name'),
  });

interface SearchResultsProps {
  search: string | null;
}

export const SearchResults = (props: Readonly<SearchResultsProps>) => {
  const { search } = props;
  const [results, setResults] = React.useState<SlideProps[]>([]);
  const [total, setTotal] = React.useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation('cta');
  const { isLoggedIn } = useReactiveVar(loginStatusVar);
  const toastError = useToastError();
  const { isLandscape } = Device.useMobileOrientation();

  const limit = 30;

  const [
    searchEvents,
    { data: eventData, error: eventError, loading: eventLoading, fetchMore: fetchMoreEvents },
  ] = useSearchEventsLazyQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const [searchSpots, { data: spotData, error: spotError, loading: spotLoading, fetchMore: fetchMoreSpots }] =
    useSearchSpotsLazyQuery({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    });

  const [
    searchMembers,
    { data: memberData, error: memberError, loading: memberLoading, fetchMore: fetchMoreMembers },
  ] = useSearchMembersLazyQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  React.useEffect(() => {
    const page = Number(searchParams.get('page')); // falls back to 0 if null
    const search = searchParams.get('search');

    const preventTrigger = preventTriggerSearch(searchParams);

    // prevent retrigger search when entiity dialog opens or closes
    if (preventTrigger) {
      return;
    }

    // handle no page or bad page
    if (search && page) {
      const currentPath = location.pathname + (location.search ?? '');
      localStorage.setItem('last-search', currentPath);

      if (search === 'spots') {
        if (!isLoggedIn) {
          const filtersFromUrl = getSpotFilters(
            searchParams,
            true // force isLoggedIn to true to get all url search params
          );

          const hasUnauthorizedFilters = Object.keys(filtersFromUrl).some((key) => !baseFilters.has(key));

          if (hasUnauthorizedFilters) {
            searchParams.forEach((value, key) => {
              if (!baseFilters.has(key)) {
                searchParams.delete(key);
              }
            });

            return setSearchParams(searchParams, {
              replace: true,
              viewTransition: true,
              preventScrollReset: true,
            });
          }
        }

        setResults([]);
        searchSpots({
          variables: {
            filters: getSpotFilters(searchParams, isLoggedIn),
            pagination: { page, limit },
          },
        });
      } else if (search === 'events') {
        setResults([]);
        searchEvents({
          variables: {
            filters: getEventFilters(searchParams),
            pagination: { page, limit },
          },
        });
      } else if (search === 'members') {
        if (!isLoggedIn) {
          searchParams.set('search', 'spots');
          return;
        }

        searchMembers({
          variables: {
            filters: getMemberFilters(searchParams),
            pagination: { page, limit },
          },
        });
      }
    }
  }, [searchParams, isLoggedIn]);

  React.useEffect(() => {
    const search = searchParams.get('search');
    if (search !== 'members' || !memberData?.searchMembers) return;

    const { total, accounts } = memberData.searchMembers;
    setTotal(total);
    setResults(
      accounts
        .map((account) => ({
          fullWidth: true,
          id: account.id,
          // type: 'Member' as 'Member',
          type: 'Spot' as 'Spot',
          to: `/search/member/${account.slug}?${searchParams.toString()}`,
          title: account.name,
          covers: account.avatar ? [account.avatar] : [],
          defaultImage: null,
          subtitleLeft: '@' + account.slug,
          subtitleRight: '',
          textBottomLeft: '',
          textBottomRight: '',
          subTextBottomLeft: '',
          subTextBottomRight: '',
        }))
        .filter((_, idx) => idx < limit)
    );
  }, [memberData, searchParams]);

  React.useEffect(() => {
    const search = searchParams.get('search');
    if (search !== 'events' || !eventData?.searchEvents) return;

    const { total, events } = eventData.searchEvents;
    setTotal(total);
    setResults(
      events
        .map((event) => ({
          fullWidth: true,
          id: event.id,
          type: 'Event' as 'Event',
          to: `/search/event/${event.id}?${searchParams.toString()}`,
          title: event.title,
          covers: event.covers,
          defaultImage: null,
          subtitleLeft: moment(event.from).format('LLLL').split(' ').slice(0, 4).join(' '),
          subtitleRight: moment(event.from).format('LT'),
          textBottomLeft: event.spot?.name || '',
          textBottomRight: '',
          subTextBottomLeft: event.category?.name
            ? t('category.' + event.category?.name, { ns: 'event' })
            : '',
          subTextBottomRight: event.spot?.address?.city || event.address?.city || t('word.unknown city'),
        }))
        .filter((_, idx) => idx < limit)
    );
  }, [eventData, searchParams]);

  React.useEffect(() => {
    const search = searchParams.get('search');
    if (search !== 'spots' || !spotData?.searchSpots) return;

    const { total, spots } = spotData.searchSpots;
    setTotal(total);
    setResults(
      spots
        .map((spot) => ({
          fullWidth: true,
          id: spot.id,
          type: 'Spot' as 'Spot',
          to: `/search/spot/${spot.slug}?${searchParams.toString()}`,
          title: spot.name,
          covers: spot.covers,
          defaultImage: spot.configuration.primaryType ? spot.configuration.primaryType + '.jpg' : null,
          subtitleLeft: [
            spot.configuration.primaryType,
            spot.configuration.secondaryType,
            spot.configuration.tertiaryType,
          ]
            .filter(Boolean)
            .map((type) => t(`types.${type}.label`, { ns: 'spot' }))
            .join(' • '),
          subtitleRight: spot.address?.city ?? '',
          textBottomLeft: getTrueKeys(spot.atmosphere)
            .map((key) => t(`atmosphere.${key}`, { ns: 'spot' }))
            .join(', '),
          textBottomRight: spot.configuration.pricing
            ? '€ ' + t(`pricing.${spot.configuration.pricing}`, { ns: 'spot' })
            : '€ ' + t(`word.${null}`, { ns: 'common' }),
          subTextBottomLeft: '',
          subTextBottomRight: '',
          SubBottomLeft: <OpenChip opening={spot.opening} closing={spot.closing} />,
          SubBottomRight: <OpenText opening={spot.opening} closing={spot.closing} />,
        }))
        .filter((_, idx) => idx < limit)
    );
  }, [spotData, searchParams]);

  React.useEffect(() => {
    if (spotError || eventError) {
      toastError(spotError || eventError);
    }
  }, [spotError, eventError]);

  const handlePageChange = (newPage: number) => {
    searchParams.set('page', newPage.toString());
    setSearchParams(searchParams);
  };

  if (!results.length || spotLoading || eventLoading) {
    return (
      <Stack width="100%" height="100%" justifyContent="center" alignItems="center">
        {spotLoading || eventLoading ? (
          <CircularProgress color="primary" />
        ) : (
          <Typography variant="body2" color="textSecondary" textAlign="center" fontStyle="italic">
            {'Pas de résultat'}
          </Typography>
        )}
      </Stack>
    );
  }

  if (Device.isDesktop) {
    return (
      <Stack width="100%" spacing={2} pb={3}>
        <Stack direction="row" width="100%">
          <Grid container spacing={2} p={2} width="100%">
            {results.map((result) => (
              <Grid key={result.id} size={{ xs: 6, sm: 6, md: 6, xl: 4 }}>
                {Device.isMobileOnly ? <MobileSlide {...result} /> : <Slide {...result} />}
              </Grid>
            ))}
          </Grid>
        </Stack>
        <Stack direction="row" flex={1} justifyContent="center">
          <Pagination
            color="primary"
            shape="rounded"
            count={Math.ceil(total / limit)}
            page={Number(searchParams.get('page')) || 1}
            onChange={(_, page) => handlePageChange(page)}
          />
        </Stack>
      </Stack>
    );
  }

  return (
    <>
      <Grid container spacing={2} width="100%">
        {results.map((result) => (
          <Grid key={result.id} size={{ xs: 12, sm: isLandscape ? 6 : 12, md: 6, xl: 4 }}>
            {Device.isMobileOnly ? <MobileSlide {...result} /> : <Slide {...result} />}
          </Grid>
        ))}
      </Grid>

      <Stack direction="row" flex={1} justifyContent="center">
        <Pagination
          color="primary"
          shape="rounded"
          count={Math.ceil(total / limit)}
          page={Number(searchParams.get('page')) || 1}
          onChange={(_, page) => handlePageChange(page)}
        />
      </Stack>
    </>
  );
};

function preventTriggerSearch(searchParams: URLSearchParams) {
  const historyState = window.history.state as { usr?: { from?: string } } | null;
  const fromUrl = historyState?.usr?.from;

  // if current has dialog params, prevent trigger
  const currHasDialog = ['e', 's', 'c'].some((k) => searchParams.has(k));
  if (currHasDialog) {
    return true;
  }

  let fromHadDialog = false;

  if (fromUrl) {
    const qIndex = fromUrl.indexOf('?');
    const fromSearch = qIndex >= 0 ? fromUrl.slice(qIndex) : '';
    const fromParams = new URLSearchParams(fromSearch.startsWith('?') ? fromSearch.slice(1) : fromSearch);
    fromHadDialog = ['e', 's', 'c'].some((k) => fromParams.has(k));
  }

  return fromHadDialog;
}
